// SPEC manna-spec.md §2.3.5 — forced TOTP enrolment for internal roles
// (added 2026-06-04).
//
// Onboarding gate. On Entra Free the ONLY thing that flips is_mfa_active=true
// for an *@emgage.com user is a verified Manna TOTP factor (§1.3.1 path 3 /
// BUG-018) — Microsoft's own MFA never reaches the Supabase JWT. Without that
// factor, baseline RLS denies every internal-role read/write, so the user
// lands in Manna and sees nothing (the bug that motivated this gate). The
// lazy step-up (RequireInternalMfa) only fires on privileged routes, so an
// un-enrolled user could wander the CRM seeing empty lists indefinitely.
//
// This gate makes enrolment mandatory: an internal-role user
// (hasInternalAppRole) whose session is not MFA-active is redirected to
// /security and cannot reach any other route until they enrol a verified
// TOTP factor. Because hook path 3 keys on the factor's mere existence (not a
// per-session challenge), enrolling once flips is_mfa_active=true on every
// future session — so this gate is a one-time onboarding step, not a
// per-login prompt.
//
// Scope:
//   - Partners / external roles: never gated (baseline-MFA-exempt, §1.4.3).
//   - Users with zero roles (fresh JIT, §2.1.7): not gated until an admin
//     grants an internal role — there's nothing to protect yet.
//   - /security itself is always reachable so the user can complete enrolment.
//   - Sign-out remains available via the Layout user menu while parked here.

import { Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../stores/auth";
import { paths } from "../routes/paths";
import { hasInternalAppRole } from "./RequireInternalMfa";

interface Props {
  children: React.ReactNode;
}

export function RequireMfaEnrollment({ children }: Props) {
  const location = useLocation();
  const appRoles = useAuthStore((s) => s.appRoles);
  const isMfaActive = useAuthStore((s) => s.isMfaActive);

  const internal = hasInternalAppRole(appRoles);
  const isSecurityRoute = location.pathname === paths.security;

  // A session that is already MFA-active necessarily has a verified factor
  // (hook path 3), so there's nothing to enrol — skip the lookup entirely.
  // We only need to inspect factors for an internal user whose session is not
  // MFA-active, to distinguish "no factor → must enrol" from the rare
  // "has factor but session not active → let lazy step-up handle it."
  const needsFactorCheck = internal && !isMfaActive;

  const factorsQuery = useQuery({
    queryKey: ["mfa_factors"],
    enabled: needsFactorCheck,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data;
    },
  });

  if (!needsFactorCheck) return <>{children}</>;
  // /security must render so the user can actually enrol.
  if (isSecurityRoute) return <>{children}</>;

  if (factorsQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasVerifiedTotp = factorsQuery.data?.totp?.some((f) => f.status === "verified") ?? false;

  // Has a factor but session isn't active (shouldn't happen on Entra Free
  // given path 3, but possible mid-refresh): don't force re-enrolment — fall
  // through and let RequireInternalMfa / MfaSessionHint handle the step-up.
  if (hasVerifiedTotp) return <>{children}</>;

  return <Navigate to={paths.security} replace state={{ enrollmentRequired: true }} />;
}
